/* eslint-disable ts/no-use-before-define */
import 'perfect-scrollbar/css/perfect-scrollbar.css'

import type { App } from 'vue'

import { throttle } from 'lodash-es'
import PerfectScrollbar from 'perfect-scrollbar'

import '../../css/scrollbar.css'
/**
 * PerfectScrollbar实例数据接口
 */
interface ScrollbarInstance {
  /** PerfectScrollbar实例 */
  ps: PerfectScrollbar
  /** ResizeObserver实例 */
  resizeObserver: ResizeObserver
  /** resize回调函数引用，用于清理 */
  resizeCallback: () => void
  /** requestAnimationFrame ID，用于取消未执行的更新 */
  rafId: number | null
  /** 配置选项的字符串化版本，用于比较 */
  optionsKey: string
}

/**
 * 使用WeakMap管理每个元素对应的滚动条实例
 * WeakMap的好处是当元素被垃圾回收时，对应的实例也会被自动清理
 */
const instanceMap = new WeakMap<Element, ScrollbarInstance>()

/**
 * 创建PerfectScrollbar实例管理器
 */
function createScrollbarManager() {
  /**
   * 为元素安装滚动条
   * @param el - 目标元素或元素选择器
   * @param options - PerfectScrollbar配置选项
   * @returns PerfectScrollbar实例
   */
  const install = (el: Element | string, options: PerfectScrollbar.Options = {}): PerfectScrollbar | null => {
    const element = typeof el === 'string' ? document.querySelector(el) : el

    if (!element) {
      console.warn('[scrollbar-directive] Element not found:', el)
      return null
    }

    // 生成配置的唯一标识，用于判断配置是否真的变化
    const optionsKey = JSON.stringify(options)

    // 如果已经存在实例，检查配置是否相同
    const existingInstance = instanceMap.get(element)
    if (existingInstance) {
      // 配置相同则直接返回现有实例，避免不必要的重建
      if (existingInstance.optionsKey === optionsKey) {
        return existingInstance.ps
      }
      // 配置不同才销毁重建
      uninstall(element)
    }

    try {
      const ps = new PerfectScrollbar(element, {
        wheelSpeed: 1,
        wheelPropagation: true, // 允许滚动传导到父元素
        scrollYMarginOffset: 5,
        suppressScrollX: true,
        ...options,
      })

      // 使用弱引用包装，避免闭包持有强引用
      const weakRef = new WeakRef(element)
      const instance: ScrollbarInstance = {
        ps,
        resizeObserver: null as unknown as ResizeObserver, // 临时值，稍后赋值
        resizeCallback: null as unknown as () => void, // 临时值，稍后赋值
        rafId: null,
        optionsKey,
      }

      const resizeCallback = throttle(() => {
        const elem = weakRef.deref()
        if (elem && instanceMap.has(elem)) {
          // 使用 requestAnimationFrame 延迟更新，避免强制重排
          if (instance.rafId !== null) {
            cancelAnimationFrame(instance.rafId)
          }
          instance.rafId = requestAnimationFrame(() => {
            instance.rafId = null
            const currentInstance = instanceMap.get(elem)
            if (currentInstance) {
              ps.update()
            }
          })
        }
      }, 150)

      const resizeObserver = new ResizeObserver(resizeCallback)
      resizeObserver.observe(element)

      // 更新实例的引用
      instance.resizeObserver = resizeObserver
      instance.resizeCallback = resizeCallback

      // 存储实例到WeakMap
      instanceMap.set(element, instance)

      return ps
    } catch (error) {
      console.error('[scrollbar-directive] Failed to create PerfectScrollbar:', error)
      return null
    }
  }

  /**
   * 卸载元素的滚动条实例
   * @param el - 目标元素
   */
  const uninstall = (el: Element): void => {
    const instance = instanceMap.get(el)

    if (!instance) {
      return
    }

    try {
      // 1. 取消未执行的 requestAnimationFrame
      if (instance.rafId !== null) {
        cancelAnimationFrame(instance.rafId)
        instance.rafId = null
      }

      // 2. 先从WeakMap中移除，防止回调中再次访问
      instanceMap.delete(el)

      // 3. 断开ResizeObserver
      instance.resizeObserver.disconnect()

      // 4. 销毁PerfectScrollbar实例（清理内部事件监听器）
      instance.ps.destroy()

      // 5. 显式清空引用，帮助垃圾回收
      // @ts-expect-error - 用于帮助垃圾回收
      instance.ps = null
      // @ts-expect-error - 用于帮助垃圾回收
      instance.resizeObserver = null
      // @ts-expect-error - 用于帮助垃圾回收
      instance.resizeCallback = null
    } catch (error) {
      console.error('[scrollbar-directive] Failed to destroy PerfectScrollbar:', error)
      // 即使出错也要确保从Map中移除
      instanceMap.delete(el)
    }
  }

  /**
   * 更新元素的滚动条实例
   * @param el - 目标元素
   */
  // const update = (el: Element): void => {
  //   const instance = instanceMap.get(el)

  //   if (!instance) {
  //     console.warn('[scrollbar-directive] No PerfectScrollbar instance found for element')
  //     return
  //   }

  //   try {
  //     console.log('[scrollbar-directive]update', instance.ps, el)
  //     instance.ps.update()
  //   } catch (error) {
  //     console.error('[scrollbar-directive] Failed to update PerfectScrollbar:', error)
  //   }
  // }

  return {
    install,
    uninstall,
  }
}

/**
 * 滚动条指令
 * 使用方法：
 * <div v-scrollbar>
 *   <div>
 *     <p>内容</p>
 *   </div>
 * </div>
 *
 * 或传入配置：
 * <div v-scrollbar="{ wheelSpeed: 2, suppressScrollX: false }">
 *   <div>
 *     <p>内容</p>
 *   </div>
 * </div>
 */
export const scrollbarDirective = {
  install(app: App) {
    const { install, uninstall } = createScrollbarManager()

    app.directive('scrollbar', {
      mounted(el: Element, binding) {
        const options = binding.value || {}
        install(el, options)
      },
      beforeUnmount(el: Element) {
        uninstall(el)
      },
      updated(el: Element, binding) {
        // 只有当绑定值引用变化时才处理
        // 这避免了在组件每次更新时都执行检查逻辑
        if (binding.value !== binding.oldValue) {
          const options = binding.value || {}
          // install 函数内部会进行深度配置比较
          install(el, options)
        }
        // 注意：如果需要响应内容变化而更新滚动条，
        // 应该由 ResizeObserver 自动处理（已在 install 中设置）
      },
    })
  },
}
